import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useStoreInfo } from "@/context/StoreInfoContext";
import { SOCIAL_PLATFORMS, socialIcon } from "@/lib/socials";

export default function StoreInfoTab() {
  const { info, refresh } = useStoreInfo();
  const [form, setForm] = useState({ email: "", phone: "", address: "Nash County, NC 27856", socials: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      email: info?.email || "",
      phone: info?.phone || "",
      address: info?.address || "Nash County, NC 27856",
      socials: (info?.socials || []).map((s) => ({ ...s })),
    });
  }, [info]);

  const addSocial = () => setForm((f) => ({ ...f, socials: [...f.socials, { platform: "instagram", label: "", url: "" }] }));
  const removeSocial = (i) => setForm((f) => ({ ...f, socials: f.socials.filter((_, idx) => idx !== i) }));
  const updateSocial = (i, key, val) =>
    setForm((f) => ({ ...f, socials: f.socials.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, socials: form.socials.filter((s) => s.url.trim()) };
      await api.put("/admin/settings", payload);
      await refresh();
      toast.success("Store info saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const placeholderFor = (platform) => SOCIAL_PLATFORMS.find((p) => p.value === platform)?.placeholder || "https://...";

  return (
    <div className="max-w-2xl space-y-8" data-testid="store-info-tab">
      <div>
        <h2 className="font-display text-2xl mb-1">Store Info</h2>
        <p className="text-sm text-muted-foreground">These appear in your website footer and legal pages. Changes go live instantly — no code needed.</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="font-mono-label text-[11px]">Business Email</Label>
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="si-email" placeholder="hello@nc-mycology.com" className="mt-1" />
        </div>
        <div>
          <Label className="font-mono-label text-[11px]">Phone (optional)</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="si-phone" placeholder="(252) 555-0123" className="mt-1" />
        </div>
        <div>
          <Label className="font-mono-label text-[11px]">Location / Address</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="si-address" placeholder="Nash County, NC 27856" className="mt-1" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="font-mono-label text-[11px]">Social Links</Label>
          <Button type="button" variant="outline" size="sm" onClick={addSocial} data-testid="si-add-social"
            className="rounded-full font-mono-label text-[10px] h-8">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add link
          </Button>
        </div>

        {form.socials.length === 0 && (
          <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-4 py-6 text-center">
            No social links yet. Click "Add link" to add Instagram, Facebook, TikTok, YouTube, and more.
          </p>
        )}

        <div className="space-y-3">
          {form.socials.map((s, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`si-social-row-${i}`}>
              <GripVertical className="w-4 h-4 text-muted-foreground shrink-0 hidden sm:block" />
              <Select value={s.platform} onValueChange={(v) => updateSocial(i, "platform", v)}>
                <SelectTrigger className="w-[150px] shrink-0" data-testid={`si-social-platform-${i}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_PLATFORMS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className="flex items-center gap-2">
                        <i className={`${p.icon} w-4 text-center`} aria-hidden="true" /> {p.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={s.url} onChange={(e) => updateSocial(i, "url", e.target.value)}
                data-testid={`si-social-url-${i}`} placeholder={placeholderFor(s.platform)} className="flex-1" />
              <button type="button" onClick={() => removeSocial(i)} data-testid={`si-social-remove-${i}`}
                className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="si-save" className="rounded-full font-mono-label text-xs px-8 h-11">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Store Info"}
      </Button>
    </div>
  );
}
